import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../css/Cart.css';
import Navbar from '../landingComponents/Navbar';
import Footer from '../landingComponents/Footer';
import TitleBar from '../landingComponents/TitleBar';
import { getCartDetails, BaseImgUrl, removeCart, prePayment, BaseUrl } from '../Services/Api';

const CartPage = (props) => {
  const { data, image } = props;
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [gatewayHtml, setGatewayHtml] = useState(null);

  const formRef = useRef(null);

  // Auto-submit payment form once gatewayHtml is set
  useEffect(() => {
    if (gatewayHtml && formRef.current) {
      formRef.current.submit();
    }
  }, [gatewayHtml]);

  // Fetch cart details
  const fetchCart = () => {
    getCartDetails()
      .then((data) => {
        // console.log(data.data.length);

        let items = [];

        if (Array.isArray(data)) {
          items = data;
        } else if (Array.isArray(data?.data)) {
          items = data.data;
        } else {
          console.warn("Unexpected cart data format:", data);
        }

        items = items.map((item) => ({
          ...item,
          quantity: item.quantity || 1,
        }));

        setCartItems(items);

        const total = items.reduce((sum, item) => {
          const price = Number(item.actual_price) || 0;
          const quantity = Number(item.quantity) || 1;
          const gst = Number(item.gst_percentage) || 0;
          const totalWithGst = (price + (price * gst) / 100) * quantity;
          return sum + totalWithGst;
        }, 0);

        setTotalAmount(total.toFixed(2));
      })
      .catch((error) => {
        console.error("Error fetching cart details:", error);
        setCartItems([]);
        setTotalAmount(0);
      });
  };

  useEffect(() => {
        document.title = 'Cart';

    fetchCart();
  }, []);

  const subtotal = cartItems.reduce((total, item) => {
    const price = Number(item.actual_price) || 0;
    const quantity = Number(item.quantity) || 1;
    return total + price * quantity;
  }, 0);

  const discount = cartItems.reduce((total, item) => {
    const price = Number(item.actual_price) || 0;
    const mrp = Number(item.mrp) || price;
    const quantity = Number(item.quantity) || 1;
    return total + (mrp - price) * quantity;
  }, 0);

  const handleQuantityChange = (id, newQuantity) => {
    if (newQuantity < 1) return;
    setCartItems(
      cartItems.map((item) =>
        item.id === id ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  // Optimistic removal
  const removeFromCart = (id) => {
    const updatedCart = cartItems.filter((item) => item.cartId !== id);
    setCartItems(updatedCart);

    const total = updatedCart.reduce((sum, item) => {
      const price = Number(item.actual_price) || 0;
      const quantity = Number(item.quantity) || 1;
      const gst = Number(item.gst_percentage) || 0;
      return sum + (price + (price * gst) / 100) * quantity;
    }, 0);
    setTotalAmount(total.toFixed(2));

    removeCart(id)
      .then((response) => {
        toast.success("Course Removed From the Cart", {
          autoClose: 2000, // 2 seconds
          // onClose: () => navigate("/login"), // Redirect after toast closes
        });


        // console.log("Item removed successfully:", response);
      })
      .catch((error) => {
        console.error("Error removing item from cart:", error);
        setCartItems((prev) => [
          ...prev,
          cartItems.find((item) => item.cartId === id),
        ]);
      });
  };

  // 🚀 Checkout function: send all category_ids
  const handleCheckout = async () => {
    const courseIds = cartItems.map((item) => item.category_id);

    try {
      const response = await prePayment(courseIds);
      // console.log(response.payment_payload);
      setGatewayHtml(response.payment_payload);
    } catch (err) {
      console.log("Checkout error:", err);
    }
  };

  return (
    <>
      <Navbar />
      <TitleBar title={data} image={image} />
      <div className="cart-page">
        <ToastContainer />
        <div className="container py-3 py-md-5">
          <div className="row">
            <div className="col-12">
              {cartItems.length > 0 ? (
                <div className="row">
                  <div className="col-lg-8 mb-4 mb-lg-0">
                    <div className="cart-items">
                      {cartItems.map((item, index) => {
                        const price = Number(item.actual_price) || 0;
                        const mrp = Number(item.mrp) || price;
                        const quantity = Number(item.quantity) || 1;
                        const gst = Number(item.gst_percentage) || 0;
                        const totalPrice =
                          (price + (price * gst) / 100) * quantity;

                        return (
                          <div
                            key={`${index}`}
                            className="cart-item mb-3 p-3"
                          >
                            <div className="row align-items-center">
                              <div className="col-4 col-md-3">
                                <img
                                  src={`${BaseImgUrl}/${item.new_category_img_url}`}
                                  alt={item.category_full_name}
                                  className="item-image img-fluid rounded"
                                />
                              </div>
                              <div className="col-8 col-md-5">
                                <h4 className="item-title mb-2">
                                  {item.category_full_name}
                                </h4>
                                <div className="item-meta d-flex flex-wrap gap-2 gap-md-3">
                                  <span className="item-rating">
                                    ⭐ {item.rating} (
                                    {item.students?.toLocaleString?.() || 0})
                                  </span>
                                  <span className="item-category badge bg-secondary d-none d-md-inline-block">
                                    Logistics
                                  </span>
                                </div>
                              </div>
                              <div className="col-12 col-md-4 mt-3 mt-md-0">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div className="item-pricing">
                                    {price < mrp && (
                                      <span className="original-price text-muted text-decoration-line-through d-block">
                                        ₹{mrp.toFixed(2)}
                                      </span>
                                    )}
                                    <span className="discounted-price fw-bold d-block">
                                      ₹{totalPrice.toFixed(2)}
                                    </span>
                                  </div>
                                  <button
                                    className="btn-remove ms-3"
                                    onClick={() => removeFromCart(item.cartId)}
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                    <span className="d-none d-md-inline ms-1">
                                      Remove
                                    </span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="col-lg-4">
                    <div className="cart-summary p-3 p-md-4 rounded">
                      <h4 className="summary-title mb-3 mb-md-4">
                        Order Summary
                      </h4>

                      <div className="summary-item d-flex justify-content-between mb-2">
                        <span>Subtotal:</span>
                        <span>₹{subtotal.toFixed(2)}</span>
                      </div>

                      {discount > 0 && (
                        <div className="summary-item d-flex justify-content-between mb-2 text-success">
                          <span>Discount:</span>
                          <span>-₹{discount.toFixed(2)}</span>
                        </div>
                      )}

                      <div className="summary-item d-flex justify-content-between mb-2">
                        <span>GST:</span>
                        <span>₹{(totalAmount - subtotal).toFixed(2)}</span>
                      </div>

                      <div className="summary-total d-flex justify-content-between mt-3 pt-3 border-top">
                        <span className="fw-bold">Total:</span>
                        <span className="fw-bold">₹{totalAmount}</span>
                      </div>
                      <form action={`${BaseUrl}/pre-pay-process`} method="POST">
                        {cartItems.map((item, index) => (
                          <input
                            key={index}
                            type="hidden"
                            name="course_ids[]"
                            value={item.category_id}
                          />
                        ))}

                        <button
                          type="submit"
                          className="btn-checkout w-100 mt-3 mt-md-4 py-2"
                          onClick={handleCheckout}
                        >
                          Proceed to Checkout
                        </button>
                      </form>


                      <p className="text-muted small mt-2 mt-md-3 mb-0">
                        *Taxes included
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="empty-cart text-center py-4 py-md-5">
                  <FontAwesomeIcon
                    icon={faShoppingCart}
                    className="empty-cart-icon mb-3 mb-md-4"
                  />
                  <h3 className="mb-2 mb-md-3">Your cart is empty</h3>
                  <p className="text-muted mb-3 mb-md-4">
                    Looks like you haven't added any items to your cart yet.
                  </p>
                  <button
                    className="btn-keep-shopping"
                    onClick={() => navigate("/")}
                  >
                    Keep Exploring                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Auto-submitting payment form */}
      {gatewayHtml && (
        <form style={{ display: "none" }}
          ref={formRef}
          action="https://lsc-india.com/payment/onlinepayment/lsc_payment_submit"
          method="POST"
        >
          <input name="source" value={gatewayHtml.source} />
          <input name="key" value={gatewayHtml.key} />
          <input name="transaction_id" value={gatewayHtml.transaction_id} />
          <input name="amount" value={gatewayHtml.amount} />
          <input name="user_id" value={gatewayHtml.user_id} />
          <input name="username" value={gatewayHtml.username} />
          <input name="email" value={gatewayHtml.email} />
          <input name="mobile" value={gatewayHtml.mobile} />
          <input name="purpose" value={gatewayHtml.purpose} />
          <input name="param1" value={gatewayHtml.param1} />
          <input name="param2" value={gatewayHtml.param2} />
          <input name="transaction_sent_dts" value={gatewayHtml.transaction_sent_dts} />
          <input name="redirect_url" value={gatewayHtml.redirect_url} />
          <input name="bcc_email" value={gatewayHtml.bcc_email} />
        </form>
      )}

      <Footer />
    </>
  );
};

export default CartPage;
